import { DailyParticipant } from '@daily-co/daily-js';
import {
  useAppMessage,
  useDaily,
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
} from '@daily-co/daily-react';
import { useCallback } from 'react';

import { useOrderedParticipantIds } from '../contexts/ParticipantsProvider';
import { HAND_RAISED_USER_DATA_KEY } from '../lib/constants';
import {
  getPrebuiltUserData,
  PREBUILT_USER_DATA_KEY,
  setPrebuiltUserData,
} from '../lib/userData';

export interface ForceLowerHandAppMessageData {
  event: 'force-lower-hand';
  isAutoLowered?: boolean;
}

export const useHasHandRaised = (id: string) => {
  const userData = useParticipantProperty(id, 'userData');
  return (
    userData?.[PREBUILT_USER_DATA_KEY]?.[HAND_RAISED_USER_DATA_KEY] ?? false
  );
};

export const useHandRaisedQueue = (id: string) => {
  const orderedParticipantIds = useOrderedParticipantIds();
  const idsWithRaisedHands = useParticipantIds({
    filter: useCallback(
      (p: DailyParticipant) =>
        // if we're in a breakout session, only get the participant IDs that are in the same breakout room
        (orderedParticipantIds.includes(p.session_id) || p.local) &&
        p.userData?.[PREBUILT_USER_DATA_KEY]?.[HAND_RAISED_USER_DATA_KEY],
      [orderedParticipantIds]
    ),
    /* Sort hand raisers by date ascending */
    sort: useCallback((a: DailyParticipant, b: DailyParticipant) => {
      const aDate =
        a.userData?.[PREBUILT_USER_DATA_KEY]?.[HAND_RAISED_USER_DATA_KEY];
      const bDate =
        b.userData?.[PREBUILT_USER_DATA_KEY]?.[HAND_RAISED_USER_DATA_KEY];
      if (aDate < bDate) return -1;
      if (aDate > bDate) return 1;
      return 0;
    }, []),
  });
  if (idsWithRaisedHands.includes(id))
    return idsWithRaisedHands.indexOf(id) + 1;
  return undefined;
};

/* A participant can't set another participant's user data. We have to send them a request using
 * sendAppMessage. Sending an app message isn't a global, "app-wide" event - if you send an app message
 * from a hook that's only loaded when a certain component is rendered (which is the case here with ParticipantMenu),
 * participants will never receive a request to update their userdata unless they have that specific menu open.
 * This is why we need to make the forceLowerHand method available on ParticipantsProvider: we need to ensure that
 * app messages are received no matter the intended receiver's app state.
 * */
export const useForceLowerHand = () => {
  const sendAppMessage = useAppMessage<ForceLowerHandAppMessageData>();

  const forceLowerHand = useCallback(
    (sessionId: string, isAutoLowered: boolean = false) => {
      sendAppMessage(
        {
          event: 'force-lower-hand',
          isAutoLowered,
        },
        sessionId
      );
    },
    [sendAppMessage]
  );

  return {
    forceLowerHand,
  };
};

export const useHandRaising = () => {
  const daily = useDaily();
  const orderedParticipantIds = useOrderedParticipantIds();
  const handRaisedParticipants = useParticipantIds({
    filter: useCallback(
      (p: DailyParticipant) =>
        (orderedParticipantIds.includes(p.session_id) || p.local) &&
        p.userData?.[PREBUILT_USER_DATA_KEY]?.[HAND_RAISED_USER_DATA_KEY],
      [orderedParticipantIds]
    ),
  });

  const localSessionId = useLocalSessionId();

  const raiseHand = useCallback(() => {
    if (!localSessionId) return;
    const prevUserData = getPrebuiltUserData(daily);
    setPrebuiltUserData(daily, {
      ...prevUserData,
      [HAND_RAISED_USER_DATA_KEY]: new Date().toISOString(),
    });
  }, [daily, localSessionId]);

  const lowerHand = useCallback(() => {
    if (!localSessionId) return;
    const prevUserData = { ...getPrebuiltUserData(daily) };
    delete prevUserData[HAND_RAISED_USER_DATA_KEY];
    setPrebuiltUserData(daily, { ...prevUserData });
  }, [daily, localSessionId]);

  return {
    raiseHand,
    lowerHand,
    handRaisedParticipants,
  };
};
